(function () {

    'use strict';

    if (typeof window === 'undefined') {

        base(
            module.exports,
            require('../../metric/metric.js'),
            require('../../metric/property.js'),
            require('../../metric/name.js'),
            require('../../metric/format.js'),
            baseRequire('models/metrics/data.js'),
            require('./dimensions.js'),
            require('../../utils/timePeriod.js'),
            require('../../utils/metric.js'),
            require('../../utils/data.js'),
            require('../../utils/async.js'),
            require('./dataSource.js'),
            require('../../data/channels.js'),
            require('../../../enums/timePeriod.js'),
            require('../../../enums/metrics.js'),
            require('lodash')
        );

    } else {

        base(
            RivalIQ.models.metrics,
            RivalIQ.metric,
            RivalIQ.metric.property,
            RivalIQ.metric.names,
            RivalIQ.metric.format,
            RivalIQ.data.metricData,
            RivalIQ.models.metrics,
            RivalIQ.utils.timePeriod,
            RivalIQ.utils.metric,
            RivalIQ.utils.data,
            RivalIQ.utils.async,
            RivalIQ.models.metrics.DataSource,
            RivalIQ.data.channels,
            RivalIQ.enums.timePeriod,
            RivalIQ.enums,
            window._
        );
    }

    function base(metrics, metricFn, propertyFn, nameFn, formatFn, metricData, dimensions, timePeriodUtils, metricUtils, dataUtils, asyncUtils, DataSource, channels, timePeriodEnums, metricEnums, _) {
        function getModels() {
            return (typeof window === 'undefined' ? require('../metric.js') : RivalIQ.models);
        }

        metrics.baseMixin = {
            modern: true,

            /*
             * Returns a new MetricDefinition with the provided properties.
             * This returns a definition instead of copying data values b/c we do
             * not know if more data will need to be requested from the server.
             */
            build: function (attrs, options) {
                const me = this;
                const models = getModels();

                const child = metricFn.build(me.attributes, attrs, options);

                if (me.get('concept')) {
                    return new models.MetricDefinition(me.get('concept'), child);
                } else {
                    return new models.MetricDefinition(child);
                }
            },

            getDataTimePeriod: function () {
                const me = this;
                return me.get('timePeriod');
            },

            getChartData: function (options) {
                const me = this;
                return me.getValue(_.defaults({
                    format: false
                }, options));
            },

            getDomainRange: function () {
                const me = this;
                // TODO I would like this to be more efficient
                const data = me.getValue({
                    format: false
                });
                return me.byDimension('domainRange', data) || me.byField('domainRange', data);
            },

            getStaticDimensionValues: function (dimension) {
                const me = this;
                return dimensions.dimensionValues(me, dimension);
            },

            getDimensionValue: function (dimension, data) {
                const me = this;
                return metricFn.dimensionValue(me.attributes, dimension, data);
            },

            getTick: function (index, options) {
                const me = this;

                options = _.defaults({}, options, {
                    format: true
                });

                if (me.get('useIntervalDomainFormatting')) {
                    const dates = me.getDataDate(index);

                    if (options.format) {
                        return me.domainFormat(dates[0], options);
                    }

                    return dates;
                }

                if (options.format) {
                    return me.domainFormat(index, options);
                }

                return [index, index];
            },

            getName: function (options) {
                const me = this;
                options = options || {};

                const override = me.byOverride('name');
                if (override) {
                    return override;
                }

                let name = nameFn.metricName(me.attributes);
                if (me.get('property') !== 'name') {
                    name = nameFn.clarifyName(me.attributes, name, options.dimensions, options.includeDimensionAggregate);
                }

                return name;
            },

            getClassName: function (data) {
                const me = this;
                data = (data ? data[data.length - 2] : data);

                let className = me.byOverride('className', data);
                if (_.isUndefined(className)) {
                    className = me.byDimension('className', data, _.last(me.getFreeDimensions()));
                }

                return className;
            },

            domainFormat: function (value, options) {
                const me = this;
                options = options || {};

                if (me.get('useIntervalDomainFormatting')) {
                    return me._intervalMetricDomainFormat(value, options);
                }

                const dimension = _.last(me.getFreeDimensions());
                return me.byDimension('format', value, dimension, options);
            },

            getDimension: function (data, dimension, key) {
                const me = this;
                const dimensions = me.getFreeDimensions();

                let dimensionIdx;
                if (_.isNumber(dimension)) {
                    dimensionIdx = dimension;
                } else {
                    dimensionIdx = _.indexOf(dimensions, dimension || dimensions.length - 1);
                }
                dimension = dimensions[dimensionIdx];

                return me.byDimension(key || 'format', data[dimensionIdx], dimension);
            },

            getSubmetricValue: _.noop,

            getDimensionName: function (options) {
                const me = this;

                return _.map(me.getFreeDimensions(), function (dimension) {
                    return me.byDimension('name', null, dimension, options);
                });
            },

            getEarliestDataKey: function () {
                const metric = this;
                const noHistoricData = metric.byField('noHistoricData');

                let earliestDataKey;
                const source = metric.get('source');
                if (metricEnums.sourceDetails[Number(source)].privateData) {

                    switch (propertyFn.hasEnoughDataKey(metric.attributes)) {
                        case 'PrivateStatisticsData':
                            return 'private_statistics_history_since';

                        case 'PrivatePresenceData':
                            return 'private_presence_history_since';

                        case 'PrivateStoriesData':
                            return 'private_stories_history_since';

                        case 'PrivatePostData':
                        default:
                            return 'private_post_history_since';
                    }
                } else {
                    if (noHistoricData) {
                        earliestDataKey = (source === metricEnums.sources.CROSS_CHANNEL ? 'firstPresenceData' : 'earliestPresenceData');
                    } else {
                        earliestDataKey = 'earliestPostData';
                    }
                }

                return earliestDataKey;
            },

            getObjectType: function () {
                const metric = this;

                const source = metric.get('source');

                if (metricEnums.sourceDetails[Number(source)].privateData) {
                    const enoughDataKey = propertyFn.getEnoughDataKey(metric.attributes);
                    return enoughDataKey === 'stories' ? enoughDataKey : 'post';
                }

                return 'post';
            },

            getEarliestDataDate: function (companyData) {
                const me = this;
                const earliestDataKey = me.getEarliestDataKey();
                const trendKey = _.get(metricEnums.sourceDetails[me.get('source')], ['trendKey']);
                // we pass in companyData on IG Insights Posts tab to avoid fetching the whole companyData object
                if (companyData) {
                    const companyId = me.get('companyId');
                    return {
                        [companyId]: companyData[earliestDataKey] || moment.utc()
                    };
                } else if (me._earliestDataDateByCompany) {
                    return me._earliestDataDateByCompany;
                } else if (_.isEmpty(me.data)) {
                    return {};
                } else if (me.data.companyData) {
                    // universal metrics only
                    return _.reduce(me.data.companyData, (memo, company) => {
                        memo[company.id] = _.get(company, [
                            'periodSummary',
                            trendKey,
                            0,
                            'dates',
                            earliestDataKey
                        ]);
                        return memo;
                    }, {});
                } else if (me.data.models) {
                    return _.reduce(me.data.models, (memo, company) => {
                        memo[company.id] = _.get(company, [
                            'attributes',
                            'periodSummary',
                            trendKey,
                            0,
                            'dates',
                            earliestDataKey
                        ]);
                        return memo;
                    }, {});
                }

                const company = me.companyData.get(me.data.companyId).toJSON();
                const record = RivalIQ.utils.companyDisplay.getSourcePrimaryRecord(me.get('source'), company);
                return {
                    [me.data.companyId]: _.get(record, ['dates', earliestDataKey])
                };
            },

            getCalculationMetadata: function () {
                const me = this;

                const data = me.data;
                const trendKey = _.get(metricEnums.sourceDetails[me.get('source')], ['trendKey']);

                if (me._calculationMetadata) {
                    return me._calculationMetadata;
                }
                if (me.get('benchmarkCompaniesFilter')) {

                    const postAggregates = _.get(data, [
                        'landscapeAggregates',
                        me.get('benchmarkCompaniesFilter'),
                        'periodSummary',
                        trendKey,
                        0,
                        'calculationMetadata'
                    ]);

                    return postAggregates;
                } else {

                    const companyId = me.get('companyId');
                    const freeDimensions = me.getFreeDimensions();
                    if (_.includes(freeDimensions, 'timeAnalysisMode')) {
                        const timeAnalysisModes = [
                            metricEnums.timeAnalysisMode.MAIN_PERIOD,
                            metricEnums.timeAnalysisMode.COMPARE_PERIOD,
                            metricEnums.timeAnalysisMode.DELTA_ABSOLUTE,
                            metricEnums.timeAnalysisMode.DELTA_RELATIVE
                        ];

                        const calculationMetadata = _.reduce(timeAnalysisModes, (memo, timeAnalysisMode) => {
                            const company = _.find(data.companyData, {
                                id: companyId
                            });
                            const isDaily = (timeAnalysisMode === metricEnums.timeAnalysisMode.DAILY_VALUES ? true : undefined);
                            const property = me.get('property');
                            const dataKey = metricUtils.timeAnalysisModeToCompanyDataKey(timeAnalysisMode, isDaily, property);

                            memo[timeAnalysisMode] = _.get(company, [
                                'periodSummary',
                                trendKey,
                                0,
                                'calculationMetadata',
                                dataKey,
                                property
                            ]);
                            return memo;
                        }, {});

                        return calculationMetadata;
                    } else {
                        const timeAnalysisMode = me.get('timeAnalysisMode');
                        const isDaily = (timeAnalysisMode === metricEnums.timeAnalysisMode.DAILY_VALUES ? true : undefined);
                        const property = me.get('property');
                        const dataKey = metricUtils.timeAnalysisModeToCompanyDataKey(timeAnalysisMode, isDaily, property);

                        const calculationMetadata = _.reduce(data.companyData, (memo, company) => {
                            memo[company.id] = _.get(company, [
                                'periodSummary',
                                trendKey,
                                0,
                                'calculationMetadata',
                                dataKey,
                                property
                            ]);
                            return memo;
                        }, {});

                        return calculationMetadata;
                    }
                }
            },

            // NOTE this method assumes data is an instance of companyData
            getValue: function (options) {
                const me = this;

                options = _.defaults(options || {}, {
                    format: true,
                    // If true, rows for private metrics will be removed from companies that do not have access to them
                    filterCompaniesWithoutPrivatePresences: true,
                    // Guarentee that every company will have at least one returned data row, even if it had no presence
                    guaranteeCompanyRow: false
                });

                if (_.exists(me.attributes.value)) {
                    return (_.isFunction(me.attributes.value) ? me.attributes.value.call(me) : me.attributes.value);
                }

                const data = me.data;

                const dimensions = _.without(me.get('dimensions'), 'companyId');

                if (_.includes(dimensions, 'post')) {
                    // posts are special as they're cross-company results.

                    if (data.privatePostAggregatesData) {
                        const resultObjects = data.privatePostAggregatesData[options.modelIdx];
                        const freeDimensions = me.getFreeDimensions();
                        return _.map(resultObjects, ({boundDimensions, value}) => {
                            // Our result format is an array where the first element(s) are the free dimension values and the last element is the value for that set of free dimensions.
                            const result = _.map(freeDimensions, freeDimension => boundDimensions[freeDimension]);
                            result.push(value);
                            return result;
                        });
                    } else {
                        const post = me.get('post');
                        if (post) {
                            return _.get(data, [post]);
                        }
                    }
                }

                if (me.get('benchmarkCompaniesFilter')) {
                    return me.extract(_.get(data, ['landscapeAggregates', me.get('benchmarkCompaniesFilter')]), dimensions, options);
                }

                if (me.get('companyId')) {
                    return me.extract(getCompany(data, me.get('companyId')), dimensions, options);
                }

                const freeDimensions = me.getFreeDimensions();
                const idx = _.indexOf(freeDimensions, 'companyId');
                let companies = me.filteredCompanyArray(options);
                if (_.isArray(options.onlyCompanyIds)) {
                    companies = _.filter(companies, function (company) {
                        return _.includes(options.onlyCompanyIds, company.id);
                    });
                }

                return _.flatten(_.map(companies, function (company) {
                    let companyData = me.extract(company, dimensions, options);
                    companyData = (_.isArray(companyData) ? companyData : [companyData]);

                    if (options.guaranteeCompanyRow && !companyData.length) {
                        // If no data was returned at all for the company
                        // insert a placeholder data item so we know it was
                        // missing
                        companyData = [_.reduce(_.range(1, freeDimensions.length), function (arr) {
                            arr.push(null);
                            return arr;
                        }, [])];
                    }

                    // Need to wrap this in an additional array as we're returning a series of values
                    // (one for each day) instead of just a solitary number.
                    if (me.attributes.timeAnalysisMode === metricEnums.timeAnalysisMode.DAILY_VALUES) {
                        companyData = [companyData];
                    }

                    _.each(companyData, function (d) {
                        d.splice(idx, 0, company.id);
                    });

                    return companyData;
                }));
            },

            filteredCompanyArray: function (options) {
                const me = this;
                const metric = me.attributes;

                if (!options.filterCompaniesWithoutPrivatePresences || !propertyFn.isPrivate(metric)) {
                    return companyDataToArray(me.data);
                }

                const source = propertyFn.source(metric);
                return channels.get(source).filterCompanies(companyDataToArray(me.data));
            },

            extract: function (company, dimensions, options) {
                const me = this;

                const staticMappings = {
                    dimensionValues: []
                };
                let dynamicDimensions = [];

                _.each(dimensions, function (dimension) {
                    if (_.isUndefined(me.get(dimension))) {
                        dynamicDimensions = _.union(dynamicDimensions, [me.remapDimension(dimension)]);
                    } else {
                        staticMappings[dimension] = me.get(dimension);
                        if (dimension === 'channel') {
                            staticMappings.source = propertyFn.source(me.attributes);
                        }
                    }
                });

                let interval;
                if (_.includes(me.get('dimensions'), 'week') && _.isUndefined(me.get('week'))) {
                    interval = timePeriodEnums.interval.weekly;
                } else if (_.includes(me.get('dimensions'), 'month') && _.isUndefined(me.get('month'))) {
                    interval = timePeriodEnums.interval.monthly;
                }
                const property = me.get('property');
                if (interval && property !== 'name') {
                    // Let's see if we already have computed values for this metric/interval since, for
                    // some things like engagement_rate_per_post, the server can do more efficiently.
                    const channelKey = metricUtils.channelToCompanyDataKey(staticMappings.channel, staticMappings.source);
                    const trendsKey = `${interval}lyValues`;

                    const loc = _.get(getPeriodSummary(company), [
                        channelKey,
                        0,
                        trendsKey
                    ]);

                    if (_.isArray(loc) && loc.length > 0) {
                        const propertyKey = metricUtils.metricToCompanyDataKey(property, staticMappings, true, me.get('aggregate'));
                        const values = _.map(loc, propertyKey);

                        // This check is slightly odd, but necessary in case companyData has some values computed for this interval,
                        // but no values for this interval + metric. In that case, we need to fallback to our normal rollup logic.
                        if (!_.isUndefined(values[0])) {
                            return _.map(values, function (value, idx) {
                                return [idx, value];
                            });
                        }
                    }
                }

                const mappingValues = _.map(dynamicDimensions, function (dimension) {
                    return metricUtils.dimensionValuesFromCompanyData(company, dimension, staticMappings.timeAnalysisMode, staticMappings.channel, me);
                });

                let mappings = [staticMappings];

                _.each(dynamicDimensions, function (dimension, i) {
                    mappings = _.flatten(_.map(mappings, function (mapping) {
                        return _.map(mappingValues[i], function (value) {
                            const tmp = {};
                            tmp[dimension] = value;
                            tmp.dimensionValues = mapping.dimensionValues.concat(value);
                            return _.extend({}, mapping, tmp);
                        });
                    }));
                });

                return me.resolveMappings(mappings, company, options, dynamicDimensions, mappingValues);
            },

            _formatValue: function (value, options) {
                const me = this;
                if (options.format === 'csv') {
                    return me.csvFormat(value, options);
                } else if (options.format) {
                    return me.format(value, options);
                }

                return value;
            },

            defaultFormat: function (data, options) {
                const me = this;

                const fn = formatFn.formatFn(me.attributes || me, data, options);
                const value = (_.isArray(data) ? data[0] : data);

                return fn(value, options);
            },

            // Maps one dimension to another. Useful for dimensions like
            // week and month which are dynamically calculated
            remapDimension: function (dimension) {
                return {
                    week: 'day',
                    month: 'day'
                }[dimension] || dimension;
            },

            resolveMappings: function (mappings, company, options, dynamicDimensions, mappingValues) {
                const me = this;
                const values = me.resolveMappingValues(mappings, company, options, dynamicDimensions, mappingValues);

                return _.map(values, function (value, i) {
                    return _.get(mappings, [i, 'dimensionValues'], []).concat(value);
                });
            },

            resolveMappingValues: function (mappings, company, options, dynamicDimensions, mappingValues) {
                const me = this;
                const rollingComparison = me.shouldComputeRollingComparison();

                if (rollingComparison) {
                    // Convert to MAIN_PERIOD b/c we want the main period daily rate of change
                    // not the rate of change relative to the compare period
                    mappings = me._convertMappingsToPeriod(mappings, metricEnums.timeAnalysisMode.MAIN_PERIOD);
                }

                const values = me.computePeriodValues(mappings, company, options, dynamicDimensions, mappingValues);

                if (rollingComparison) {
                    return dataUtils.rollupDeltaValues(values, me.get('timeAnalysisMode'));
                }

                return values;
            },

            comparisonValues: function (values, timeAnalysisMode) {
                const result = [null];

                for (let i = 1; i < values.length; i++) {
                    if (_.isNull(values[i]) || _.isNull(values[i - 1])) {
                        return null;
                    }

                    let value = values[i] - values[i - 1];
                    if (timeAnalysisMode === metricEnums.timeAnalysisMode.DELTA_RELATIVE) {
                        if (values[i - 1] === 0) {
                            value = 0; // BUG BUG what is correct here?
                        } else if (value !== 0) {
                            value = value / values[i - 1];
                        }
                    }

                    result.push(value);
                }

                return result;
            },

            // Sets the timeAnalysisMode to the specified period for all passed in
            // mappings.
            _convertMappingsToPeriod: function (mappings, period) {
                return _.map(mappings, function (mapping) {
                    return _.defaults({
                        timeAnalysisMode: period
                    }, mapping);
                });
            },

            shouldComputeRollingComparison: function () {
                const me = this;
                return me.hasPeriodDimension() &&
                    _.includes([metricEnums.timeAnalysisMode.DELTA_RELATIVE,
                        metricEnums.timeAnalysisMode.DELTA_ABSOLUTE], me.get('timeAnalysisMode'));
            },

            hasPeriodDimension: function () {
                const me = this;

                return _.some(me.get('dimensions'), function (dimension) {
                    return dimension === 'day' || dimension === 'week' || dimension === 'month';
                });
            },

            // Returns mapping values, rolling up by period size if applicable
            computePeriodValues: function (mappings, company, options, dynamicDimensions, mappingValues) {
                const me = this;

                // Add custom handling here
                let interval;
                if (_.includes(me.get('dimensions'), 'week')) {
                    interval = timePeriodEnums.interval.weekly;
                }

                if (_.includes(me.get('dimensions'), 'month')) {
                    interval = timePeriodEnums.interval.monthly;
                }

                return me.rollupValues(interval, mappings, company, options, dynamicDimensions, mappingValues);
            },

            getStaticPropertyValue: function (company, property, mapping) {
                const me = this;

                // NOTE special case for name handling.
                // TODO stop trying to handle dimensions as metrics
                if (property === 'name') {
                    const dimension = _.last(metricFn.getFreeDimensions(me.attributes));
                    if (dimension === 'companyId') {
                        return company.id;
                    }

                    return mapping[dimension];
                }

                const channelKey = metricUtils.channelToCompanyDataKey(mapping.channel, mapping.source);
                const trendsKey = metricUtils.timeAnalysisModeToCompanyDataKey(mapping.timeAnalysisMode, mapping.day, property);
                const dayKey = mapping.day;
                const daily = (!_.isUndefined(dayKey) || mapping.timeAnalysisMode === metricEnums.timeAnalysisMode.DAILY_VALUES);
                const propertyKey = metricUtils.metricToCompanyDataKey(property, mapping, daily, me.get('aggregate'));

                const loc = _.get(getPeriodSummary(company), _.reject([
                    channelKey,
                    0,
                    trendsKey,
                    dayKey
                ], _.isUndefined));

                // return all the daily values if we're looking at daily time analysis mode and they haven't specified a dayKey.
                if (mapping.timeAnalysisMode === metricEnums.timeAnalysisMode.DAILY_VALUES && _.isUndefined(dayKey)) {
                    return _.map(loc, propertyKey);
                }

                return _.get(loc, propertyKey, null);
            },

            getPropertyValues: function (company, property, mappings, options) {
                const me = this;

                if (!_.isArray(mappings)) {
                    return me.getPropertyValue(company, property, mappings, options);
                }

                return _.map(mappings, function (mapping) {
                    return me.getPropertyValue(company, property, mapping, options);
                });
            },

            getPropertyValue: function (company, property, mapping, options) {
                const me = this;

                if (_.isNull(mapping)) {
                    return null;
                }

                const value = me.getStaticPropertyValue(company, property, mapping);
                return me._formatValue(value, options);
            },

            rollupValues: function (interval, mappings, company, options, dynamicDimensions, mappingValues) {
                const me = this;

                const count = mappings.length;
                let startDate;

                if (interval && count) {
                    const dates = getPeriodSummary(company).dates;
                    startDate = dates[metricUtils.companyDataStartDateKey(mappings[0].timeAnalysisMode)];
                }

                const dataSource = new DataSource(me, company, options);

                let allDimensionResults = [];

                // DFS traversal of the free dimensions + mappingValues to make sure allDimensionResults are in the right order.
                // For example, if the free dimensions are [day, channel] and the values are [0, 1, 2, 3], ['fb', 'ig', 'tw'],
                // allDimensionResults will have results in order:
                // day = 0, channel = 'fb'
                // day = 0, channel = 'ig'
                // day = 0, channel = 'tw'
                // day = 1, channel = 'fb'
                // day = 1, channel = 'ig'
                // day = 1, channel = 'tw'
                // ...
                // We'll flatten/zip it on the way out so that it's all in one level.
                function dfsCalculateResults(fixedDimensions, freeDimensions) {

                    // base-case, all the dimensions have been fixed. Calculate the value for this set of dimension values.
                    if (freeDimensions.length === 0) {
                        const relevantMappings = _.filter(mappings, fixedDimensions);

                        const resultsForMappingValue = _.map(timePeriodUtils.groupByInterval(relevantMappings, interval, startDate, true), function (intervalChunk) {
                            return dataUtils.rollup(dataSource, intervalChunk);
                        });

                        allDimensionResults = allDimensionResults.concat([resultsForMappingValue]);
                        return;
                    }

                    const freeDimension = _.head(freeDimensions);

                    if (freeDimension === 'day') { // NOTE: this will be "day" even if the metric has "week" or "month" as it's describing the underlying data, not the desired output.
                        return dfsCalculateResults(fixedDimensions, _.tail(freeDimensions));
                    } else {
                        const mappingValuesIdx = _.indexOf(dynamicDimensions, freeDimension);
                        _.each(mappingValues[mappingValuesIdx], function (freeDimensionValue) {

                            const filter = _.clone(fixedDimensions);
                            filter[freeDimension] = freeDimensionValue;

                            return dfsCalculateResults(filter, _.tail(freeDimensions));
                        });
                    }

                }

                dfsCalculateResults({}, dynamicDimensions);

                return _.flatten(_.zip.apply(_, allDimensionResults));
            },

            // Serialize metric with all relevant options so that export can
            // recreate the state
            serialize: function () {
                const me = this;
                return me.toJSON();
            }
        };

        metrics.Base = _.extend({}, metrics.baseMixin, {
            dataSource: 'postgres',
            _buildMetricFromData: function (data) {
                const me = this;
                const models = getModels();
                const metric = new models.Metric(me.resolvedAttributes(), data);
                metric.companyData = data;
                return metric;
            },
            resolvedAttributes: function () {
                const me = this;
                const models = getModels();

                return _.extend(models.Metric.prototype.resolvedAttributes.call(me), {
                    source: propertyFn.source(me.attributes)
                });
            },
            // used for server side fetches only
            fetchAsync: async function () {
                const me = this;
                const args = Array.prototype.splice.call(arguments, 0);
                const metricDataArgs = [me].concat(args);

                const fetchPromise = metricData.companyData.fetchAsync.apply(null, metricDataArgs);
                const data = await fetchPromise;
                return me._buildMetricFromData(data);
            },
            // used for client side fetches only
            fetch: asyncUtils.deferredOrCallback(function () {
                const me = this;
                const args = Array.prototype.splice.call(arguments, 0);
                const callback = _.last(args);
                // Remove callback from args
                args.pop();

                const metricDataArgs = [me].concat(args).concat(function (error, data) {
                    if (error) {
                        return callback(error);
                    }

                    return callback(null, me._buildMetricFromData(data));
                });

                metricData.companyData.fetch.apply(null, metricDataArgs);
            })
        });

        // NOTE company is a Backbone.Model on the client and a plain object on
        // the server
        function getPeriodSummary(company) {
            return (company.get ? company.get('periodSummary') : company.periodSummary);
        }

        function getCompany(companyData, id) {
            if (companyData.get) {
                return companyData.get(id);
            }

            companyData = companyDataToArray(companyData);
            return _.find(companyData, {
                id: id
            });
        }

        function companyDataToArray(companyData) {
            if (_.isArray(companyData)) {
                return companyData;
            }

            if (companyData.companyData) {
                return companyDataToArray(companyData.companyData);
            }

            return companyData.toArray();
        }

        metrics.build = function (props, attrs) {
            const models = getModels();
            attrs = _.extend({}, attrs, {
                dimensions: _.union(attrs.dimensions || [], props.dimensions)
            });

            return new models.MetricDefinition(_.create(props, attrs));
        };
    }
})();
